import React, { useContext, useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, Button } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { useAptorApi } from '../../../../../../../Api';
import { UserContext } from '../../../../../../../Context/UserContext/UserContext';
import { SelectOption } from '../../../../../../../Components/Form/Form.types';
import { EvaluationInfoEnum } from '../../../../../../../Context/EvaluationInfoEmun';
import UpdateConnectionInfo from '../../../UpdateConnectionWizard/UpdateConnectionInfo';
import Step1SelectCompanyUnit from './Step1SelectCompanyUnit';
import Step2EditChecklist from './Step2EditChecklist';

interface EditChecklistWizardProps {
  open: boolean;
  onClose: () => void;
  lawId: number;
  onComplete?: () => void;
}

const PRIMARY = '#c4122f';

const DEFAULT_STEP_INFO = {
  'select-unit': {
    OurLawCheckListInfoSv: 'select-unit',
    OurLawCheckListInfoEn: 'select-unit',
  },
  'edit-checklist': {
    OurLawCheckListInfoSv: 'edit-checklist',
    OurLawCheckListInfoEn: 'edit-checklist',
  },
};

export const EditChecklistWizard: React.FC<EditChecklistWizardProps> = ({ open, onClose, lawId, onComplete }) => {
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();
  const { companyUnits, getCompanyUnitName } = useContext(UserContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedUnit, setSelectedUnit] = useState<SelectOption | null>(null);
  const [updateChecklistInfo, setUpdateChecklistInfo] = useState<any>(null);

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedUnit(null);
    onClose();
  };

  const handleBack = () => {
    if (currentStep === 0) {
      handleClose();
    } else {
      setCurrentStep(0);
    }
  };

  const handleUnitSelect = (unit: SelectOption) => {
    setSelectedUnit(unit);
    setCurrentStep(1);
  };

  const handleCompleteStep2 = () => {
    if (onComplete) onComplete();
    handleClose();
  };

  const fetchUpdateChecklistStepInfo = React.useCallback(() => {
    api
      .getLawportalStepSettings(EvaluationInfoEnum.OurLawCheckListInfo)
      .then((result) => {
        if (abortController.current.signal.aborted) return;
        if (result) {
          try {
            const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
            setUpdateChecklistInfo(parsedResult);
          } catch (error) {
            console.error('Error parsing evaluation step info:', error);
            setUpdateChecklistInfo(DEFAULT_STEP_INFO);
          }
        } else {
          setUpdateChecklistInfo(DEFAULT_STEP_INFO);
        }
      })
      .catch((err) => {
        console.error('API error fetching step settings:', err);
        setUpdateChecklistInfo(DEFAULT_STEP_INFO);
      });
  }, [api, abortController]);

  useEffect(() => {
    if (open) {
      fetchUpdateChecklistStepInfo();
    }
  }, [open, fetchUpdateChecklistStepInfo]);

  const getCurrentStepName = () => {
    return currentStep === 0 ? 'select-unit' : 'edit-checklist';
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Step1SelectCompanyUnit
            onNext={handleUnitSelect}
            onBack={handleBack}
            onCancel={handleClose}
            isFirstStep={true}
            lawId={lawId}
            selectedCompanyUnit={selectedUnit || undefined}
            getCompanyUnitName={getCompanyUnitName}
            updateChecklistInfo={updateChecklistInfo}
            currentStepName="select-unit"
            setUpdateChecklistInfo={fetchUpdateChecklistStepInfo}
          />
        );
      case 1:
        return selectedUnit ? (
          <Step2EditChecklist
            lawId={lawId}
            unitId={selectedUnit.value as number}
            onBack={handleBack}
            onCancel={handleClose}
            onComplete={handleCompleteStep2}
            updateChecklistInfo={updateChecklistInfo}
            currentStepName="edit-checklist"
            setUpdateChecklistInfo={fetchUpdateChecklistStepInfo}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth disableEscapeKeyDown>
      <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          {formatMessage({ id: 'law-portal.our-laws.overview.edit-checklist-wizard', defaultMessage: 'Edit Checklist' })}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '24px', minHeight: '500px', position: 'relative' }}>
        <Box style={{ minHeight: '520px' }}>
          <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Button variant="outlined" onClick={handleClose} style={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 700 }}>
                {formatMessage({ id: 'form.cancel' })}
              </Button>
            </Box>
            {currentStep > 0 && (
              <Button variant="outlined" onClick={handleBack} style={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 700 }}>
                {formatMessage({ id: 'component.back-button.back' })}
              </Button>
            )}
          </Box>

          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', marginBottom: '24px', backgroundColor: '#fdf2f3', borderRadius: '6px', borderLeft: `4px solid ${PRIMARY}`, flexWrap: 'wrap', gap: '8px' }}>
            <Typography variant="body2" style={{ fontWeight: 700, color: PRIMARY, fontSize: '13px' }}>
              {formatMessage({ id: 'law-portal.evalution.plan.step' }, { currentStep: currentStep + 1, totalSteps: 2 })}
            </Typography>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {Array.from({ length: 2 }).map((_, i) => (
                <Box key={i} style={{ width: '20px', height: '8px', borderRadius: '4px', backgroundColor: i < currentStep + 1 ? PRIMARY : '#e0e0e0', transition: 'all 0.3s ease' }} />
              ))}
            </Box>
          </Box>

          {renderStep()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
